import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { StatsigFeatureKeys } from 'src/features';
import { useStatsigGate } from 'src/hooks/statsig';

import type { RecentSearch } from '../types/recent-search';
import {
  addRecentSearch as addToStorage,
  getRecentSearches as getFromStorage,
  subscribe,
} from '../utils/recent-search-storage';

interface UseRecentSearchesResult {
  recentSearches: RecentSearch[];
  addRecentSearch: (query: string) => void;
}

const EMPTY: RecentSearch[] = [];

export function useRecentSearches(
  workspaceSlug: string
): UseRecentSearchesResult {
  const isEnabled = useStatsigGate(StatsigFeatureKeys.isNewCodeSearchEnabled);

  const getSnapshot = useCallback(
    () => (isEnabled && workspaceSlug ? getFromStorage(workspaceSlug) : EMPTY),
    [isEnabled, workspaceSlug]
  );

  const recentSearches = useSyncExternalStore(subscribe, getSnapshot);

  const addRecentSearch = useCallback(
    (query: string) => {
      if (isEnabled && workspaceSlug) {
        addToStorage(workspaceSlug, query);
      }
    },
    [isEnabled, workspaceSlug]
  );

  return useMemo(
    () => ({ recentSearches, addRecentSearch }),
    [recentSearches, addRecentSearch]
  );
}
